import { Skeleton } from '@/components/ui/skeleton';
import { PageHeaderSkeleton } from './PageHeaderSkeleton';
import { PageWrapper } from '@/components/layout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

/**
 * Skeleton for the VoteComparison page.
 * Reserves layout space to prevent CLS.
 *
 * Layout breakdown:
 * - PageHeader with gradient + breadcrumbs: ~140px
 * - YearSelector card: ~56px
 * - Legislator selector card: ~100px
 * - Filter controls card: ~100px
 * - Agreement summary cards row: ~100px
 * - Table placeholder: ~300px
 */
export function VoteComparisonPageSkeleton() {
  return (
    <PageWrapper
      header={
        <PageHeaderSkeleton variant="gradient" showBreadcrumbs={true} showDescription={true} />
      }
    >
      <div className="space-y-6 mb-12">
        {/* YearSelector card */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-9 w-[180px]" />
          </div>
        </div>

        {/* Legislator Selector Card */}
        <Card>
          <CardHeader className="pb-3">
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>

        {/* Filter Controls Card */}
        <Card>
          <CardHeader className="pb-3">
            <Skeleton className="h-6 w-20" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex flex-col gap-1">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-10 w-full sm:w-[160px]" />
              </div>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-3 w-6" />
                <Skeleton className="h-10 w-full sm:w-[160px]" />
              </div>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-10 w-full sm:w-[180px]" />
              </div>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-10 w-full sm:w-[180px]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agreement Summary Cards */}
        <div>
          <Skeleton className="h-6 w-36 mb-3" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-6" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <div className="text-right">
                      <Skeleton className="h-8 w-16" />
                      <Skeleton className="h-3 w-16 mt-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Table placeholder */}
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          {/* Table header */}
          <div className="border-b border-border p-4">
            <div className="flex gap-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-20 ml-auto" />
            </div>
          </div>
          {/* Table rows */}
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="border-b border-border p-4 last:border-b-0">
              <div className="flex gap-4 items-center">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-6 w-16 ml-auto rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageWrapper>
  );
}
